/**
 * Phase 9.1 — Cultural / Political Contextual Risk Layer schema.
 *
 * Most surveillance tools treat all jurisdictions as fungible. The reality
 * is that the same outbreak in different contexts requires radically
 * different responses — and missing this is a known failure mode (Ebola
 * West Africa 2014 burial practices, polio Northern Nigeria 2003 fatwa,
 * HPV Japan 2013 narrative collapse, COVID US political polarization).
 *
 * This module defines the per-(jurisdiction × pathogen-class × domain)
 * data shape the §7.x Cultural Risk Layer surface consumes, plus the
 * registry-load + query API. Seed entries land in Phase 9.2.
 *
 * Complements (does NOT replace) the existing per-region `CulturalSpec`
 * in `types.ts`. CulturalSpec is light-touch per-region (greeting norms,
 * mask acceptance, festivals); CulturalRiskFactor is the rich
 * pathogen-aware register with citation enforcement.
 *
 * ## Anti-stereotyping rails (load-bearing — see plan §D)
 *
 *   - Every claim carries a citation that round-trips (PMID/DOI/URL).
 *   - Contested claims require ≥2 independent citations (validator gate).
 *   - `politically-contested` flag requires human-curator sign-off in
 *     `reviewedBy` (no agent-only commits — CI policy).
 *   - `sensitivityFlags` populated MUST trigger second-pair-of-eyes
 *     review before publish.
 *
 * The verifier-first + sign-off rules keep this from becoming a
 * stereotype-generator. Stereotyping risk is real; the schema
 * actively defends against it.
 */

// Browser-safe: JSON imported directly (Vite + Node both support assertion-
// free JSON imports under "type": "module"). No node:fs / node:path so the
// loader runs in the browser bundle.
import registryJson from '../data/cultural-risk-factors.json';
// Phase 9.3 — the politically-contested staging file. Held OUT of the live
// register (loadCulturalRiskRegistry never reads it) until a human curator
// signs off via the §7.23 Operator Workspace "Curate" queue, which loads it
// through loadPendingCulturalRiskReview() below. Bundled JSON — browser-safe.
import pendingReviewJson from '../data/cultural-risk-factors._pending_human_review.json';

// ---------------------------------------------------------------------------
// Pathogen classes — the broad transmission-mode buckets cultural-risk
// factors hang off. Kept coarse so a single register entry covers every
// filovirus (Ebola, Marburg, etc.) or every faecal-oral pathogen (cholera,
// rotavirus, hepatitis A, typhoid, STEC) rather than re-entering the same
// burial-practice / WASH-blocker factor per specific catalog id.
// ---------------------------------------------------------------------------

export type PathogenClass =
  /** Filovirus (Ebola, Marburg) + arenavirus (Lassa) hemorrhagic fevers. */
  | 'filovirus'
  /** Respiratory droplet + airborne (flu, RSV, SARS-CoV-2, measles, MERS). */
  | 'respiratory-droplet'
  /** Faecal-oral (cholera, rotavirus, hep A/E, typhoid, STEC, polio). */
  | 'faecal-oral'
  /** Vector-borne (dengue, malaria, Zika, chikungunya, oropouche, WNV). */
  | 'vector-borne'
  /** Sexually transmitted + blood-borne (HIV, mpox, syphilis, HCV). */
  | 'STI-BBV'
  /** Zoonotic spillover events (brucellosis, rabies, RVF, Q fever, melioidosis). */
  | 'zoonotic-spillover'
  /** Healthcare-associated (C. difficile, CRE, ESBL, MRSA, Candida auris). */
  | 'healthcare-associated';

// ---------------------------------------------------------------------------
// Domains — the 10 categories of cultural / political factor the research
// pass identified. Each domain has its own evidence pattern + operator
// surface treatment.
// ---------------------------------------------------------------------------

export type CulturalRiskDomain =
  /** Funeral / death practices that affect transmission (corpse handling). */
  | 'burial-practice'
  /** Vaccine hesitancy patterns rooted in specific historical triggers. */
  | 'vaccine-hesitancy'
  /** Religious / civic mass gatherings that drive transmission events. */
  | 'mass-gathering'
  /** Gender restrictions affecting case detection + vaccination access. */
  | 'gender-access'
  /** Animal husbandry / bushmeat / wet-market spillover interfaces. */
  | 'animal-interface'
  /** Conflict, displacement, sanctions impact on surveillance + response. */
  | 'conflict-sanctions'
  /** Government denialism / data suppression patterns. */
  | 'govt-denialism'
  /** Stigma-driven under-ascertainment (MSM, PWID, caste, indigenous). */
  | 'stigma-underreport'
  /** WASH cultural blockers (open-defecation, ritual purity, water scarcity). */
  | 'wash-blocker'
  /** Health-system trust indices (Wellcome, VCP, Edelman, etc.). */
  | 'trust-index';

export type EvidenceStrength = 'strong' | 'moderate' | 'contested' | 'speculative';
export type Temporality = 'active' | 'historical' | 'dormant' | 'seasonal';

export type SensitivityFlag =
  | 'stereotyping-risk'
  | 'politically-contested'
  | 'religious-sensitivity'
  | 'stigma-amplification-risk';

export type RecommendedMessenger =
  | 'religious-leader'
  | 'community-health-worker'
  | 'female-peer'
  | 'elder'
  | 'clinician';

export type OperatorSurfaceContext =
  | 'minister-brief'
  | 'campaign-planner'
  | 'contact-tracing'
  | 'case-dashboard'
  | 'preparedness';

export interface CulturalRiskCitation {
  publisher: string;
  title: string;
  /** PMID — round-trips via @biomax/plugin-literature-fetch fetchByPmid. */
  pmid?: string;
  /** DOI — alternative to PMID for non-PubMed sources. */
  doi?: string;
  /** URL — last-resort identifier for grey literature / WHO sitreps. */
  url?: string;
  /** Year — required for ranking + recency triage. */
  year?: number;
}

export interface CulturalRiskExampleCase {
  year: number;
  location: string;
  /** One-sentence impact summary. NO stereotyped attribution. */
  impact: string;
  citation: CulturalRiskCitation;
}

export interface CulturalRiskOperatorAction {
  surfaceContext: OperatorSurfaceContext;
  /** Markdown template ref relative to repo root, e.g. "templates/safe-dignified-burial.md". */
  template?: string;
  recommendedMessenger?: readonly RecommendedMessenger[];
}

/**
 * One register entry. Per (jurisdiction × pathogenClass × domain).
 *
 * IMPORTANT: every numeric or named claim in `phenomenon` or
 * `exampleCases[].impact` must be sourced via `exampleCases[].citation`.
 * Contested or politically-contested entries have higher bars (see audit
 * gate + sensitivityFlags rules).
 */
export interface CulturalRiskFactor {
  /** Hierarchical id — e.g. "burial.sierraleone.filovirus.2014". */
  id: string;
  jurisdiction: {
    /** ISO-3 country code. */
    iso3: string;
    /** Optional subnational scope ("Kailahun District", "Kano State"). */
    subnational?: string;
  };
  pathogenClass: PathogenClass;
  domain: CulturalRiskDomain;
  /** 1-2 sentence phenomenon summary. Sourced via exampleCases citations. */
  phenomenon: string;
  evidenceStrength: EvidenceStrength;
  temporality: Temporality;
  /** Optional seasonal hint for mass-gathering / calendar-driven factors. */
  seasonality?: {
    type: 'calendar';
    /** Cron-style expression for periodic events. */
    cron?: string;
    /** Next event ISO date. */
    nextEvent?: string;
  };
  exampleCases: readonly CulturalRiskExampleCase[];
  operatorAction: CulturalRiskOperatorAction;
  /**
   * Multiplicative bias factor on reported case curves derived from a
   * governance index (V-Dem, RSF, Freedom House). 1.0 = no adjustment;
   * >1.0 = "reported curve likely undercounts true incidence." Source
   * MUST be cited in the operator-warnings — never inferred without one.
   */
  dataConfidenceModifier?: number;
  sensitivityFlags: readonly SensitivityFlag[];
  lastReviewed: string;   // ISO date
  reviewedBy: string;     // human curator id; agent-only commits BLOCKED
                          // for politically-contested entries via audit gate
}

// ---------------------------------------------------------------------------
// Loader — JSON is imported at module load (Vite + Node both bundle it).
// Browser-safe; no node:fs.
// ---------------------------------------------------------------------------

let cachedRegistry: readonly CulturalRiskFactor[] | null = null;

/** Load the cultural-risk register (cached). Returns empty array when the
 *  bundled JSON has no entries — Phase 9.2 seeds them. */
export function loadCulturalRiskRegistry(): readonly CulturalRiskFactor[] {
  if (cachedRegistry !== null) return cachedRegistry;
  const parsed = registryJson as { entries?: CulturalRiskFactor[] };
  cachedRegistry = Object.freeze(parsed.entries ?? []);
  return cachedRegistry;
}

// Test-only escape hatch — never used in production code paths.
export function __setCulturalRiskRegistry(entries: readonly CulturalRiskFactor[]): void {
  cachedRegistry = Object.freeze([...entries]);
}
export function __resetCulturalRiskRegistry(): void {
  cachedRegistry = null;
}

// ---------------------------------------------------------------------------
// Phase 9.3 — pending-human-review loader. These entries are HELD from the
// live register precisely because they're politically-contested (the audit
// gate Rule 3 blocks agent-only commits of `politically-contested` claims).
// The §7.23 Operator Workspace "Curate" queue surfaces them so a human
// curator can sign off; until they do, loadCulturalRiskRegistry() never
// returns them. Browser-safe: the JSON is bundled at module load, same as
// the main register — no node:fs.
// ---------------------------------------------------------------------------

let cachedPendingReview: readonly CulturalRiskFactor[] | null = null;

/** Load the entries awaiting human curator review (politically-contested
 *  rail — agent-authored entries blocked from the live register until a
 *  human curator signs off). Cached. Browser-safe (bundled JSON). */
export function loadPendingCulturalRiskReview(): readonly CulturalRiskFactor[] {
  if (cachedPendingReview !== null) return cachedPendingReview;
  const parsed = pendingReviewJson as { entries?: CulturalRiskFactor[] };
  cachedPendingReview = Object.freeze(parsed.entries ?? []);
  return cachedPendingReview;
}

// ---------------------------------------------------------------------------
// Query API — composable per (jurisdiction, pathogenClass, domain).
// ---------------------------------------------------------------------------

export interface CulturalRiskQuery {
  iso3?: string;
  subnational?: string;
  pathogenClass?: PathogenClass;
  domain?: CulturalRiskDomain;
  /** Filter by minimum evidence strength. Default 'speculative' (all). */
  minEvidence?: EvidenceStrength;
  /** Filter by temporality. Default: all. */
  temporality?: Temporality | readonly Temporality[];
  /** Exclude entries with any of these sensitivity flags. */
  excludeFlags?: readonly SensitivityFlag[];
}

const EVIDENCE_RANK: Record<EvidenceStrength, number> = {
  speculative: 0, contested: 1, moderate: 2, strong: 3,
};

export function queryCulturalRiskFactors(query: CulturalRiskQuery = {}): readonly CulturalRiskFactor[] {
  const all = loadCulturalRiskRegistry();
  const minRank = query.minEvidence ? EVIDENCE_RANK[query.minEvidence] : 0;
  const temporalitySet = query.temporality
    ? new Set<Temporality>(Array.isArray(query.temporality) ? query.temporality : [query.temporality])
    : null;
  const excludeFlagSet = query.excludeFlags ? new Set<SensitivityFlag>(query.excludeFlags) : null;
  return all.filter(f => {
    if (query.iso3 && f.jurisdiction.iso3 !== query.iso3) return false;
    if (query.subnational && f.jurisdiction.subnational !== query.subnational) return false;
    if (query.pathogenClass && f.pathogenClass !== query.pathogenClass) return false;
    if (query.domain && f.domain !== query.domain) return false;
    if (EVIDENCE_RANK[f.evidenceStrength] < minRank) return false;
    if (temporalitySet && !temporalitySet.has(f.temporality)) return false;
    if (excludeFlagSet) {
      for (const flag of f.sensitivityFlags) {
        if (excludeFlagSet.has(flag)) return false;
      }
    }
    return true;
  });
}

/** Look up by id. Returns undefined when absent. */
export function findCulturalRiskFactor(id: string): CulturalRiskFactor | undefined {
  return loadCulturalRiskRegistry().find(f => f.id === id);
}

/** Aggregate `dataConfidenceModifier` across factors matching a query.
 *  Returns the MAXIMUM modifier (most-undercounting); null when no
 *  factor in scope has a modifier set. Used by case-curve overlays
 *  to show "estimated-true" ghost lines. */
export function dataConfidenceFor(query: CulturalRiskQuery = {}): number | null {
  const factors = queryCulturalRiskFactors(query);
  let max: number | null = null;
  for (const f of factors) {
    if (typeof f.dataConfidenceModifier === 'number') {
      max = max === null ? f.dataConfidenceModifier : Math.max(max, f.dataConfidenceModifier);
    }
  }
  return max;
}

// ---------------------------------------------------------------------------
// Audit gate — call from CI to refuse a register that violates the
// anti-stereotyping rails.
// ---------------------------------------------------------------------------

export interface CulturalRiskAuditFinding {
  id: string;
  severity: 'error' | 'warning';
  message: string;
}

export function auditCulturalRiskRegistry(): readonly CulturalRiskAuditFinding[] {
  const findings: CulturalRiskAuditFinding[] = [];
  const seen = new Set<string>();
  for (const f of loadCulturalRiskRegistry()) {
    if (seen.has(f.id)) {
      findings.push({ id: f.id, severity: 'error', message: `Duplicate id "${f.id}"` });
    }
    seen.add(f.id);

    // Rule 1: every entry needs at least one example case + citation.
    if (f.exampleCases.length === 0) {
      findings.push({ id: f.id, severity: 'error', message: 'No exampleCases — every claim must be sourced' });
    }
    for (const ex of f.exampleCases) {
      if (!ex.citation.pmid && !ex.citation.doi && !ex.citation.url) {
        findings.push({ id: f.id, severity: 'error', message: 'Citation missing identifier (pmid/doi/url)' });
      }
    }

    // Rule 2: contested or speculative claims require ≥2 independent citations.
    if ((f.evidenceStrength === 'contested' || f.evidenceStrength === 'speculative') && f.exampleCases.length < 2) {
      findings.push({
        id: f.id, severity: 'error',
        message: `evidenceStrength="${f.evidenceStrength}" requires ≥2 independent citations (have ${f.exampleCases.length})`,
      });
    }

    // Rule 3: politically-contested flag requires human-curator sign-off.
    if (f.sensitivityFlags.includes('politically-contested')) {
      if (!f.reviewedBy || f.reviewedBy.includes('agent') || f.reviewedBy === 'unknown') {
        findings.push({
          id: f.id, severity: 'error',
          message: 'politically-contested entries require a human reviewedBy (no agent-only commits)',
        });
      }
    }

    // Rule 4: dataConfidenceModifier needs a citation in operatorWarnings or example cases.
    if (typeof f.dataConfidenceModifier === 'number' && f.exampleCases.length === 0) {
      findings.push({
        id: f.id, severity: 'error',
        message: 'dataConfidenceModifier must be sourced via exampleCases citation',
      });
    }

    // Rule 5: lastReviewed must be present + ISO-formatted.
    if (!f.lastReviewed || !/^\d{4}-\d{2}-\d{2}/.test(f.lastReviewed)) {
      findings.push({
        id: f.id, severity: 'error',
        message: 'lastReviewed missing or not ISO-date',
      });
    }

    // Warning: any sensitivity flag → flag for second-pair-of-eyes review
    if (f.sensitivityFlags.length > 0 && f.reviewedBy.startsWith('claude')) {
      findings.push({
        id: f.id, severity: 'warning',
        message: `sensitivityFlags=[${f.sensitivityFlags.join(', ')}] — agent-authored; needs second-pair-of-eyes review`,
      });
    }
  }
  return findings;
}
